import {
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  SaveOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import trimText from '@helpers/trim-text';
import useAuth from '@hooks/use-auth';
import { useCooldown } from '@hooks/use-cooldown';
import { languages } from '@interfaces/language';
import {
  MenuItemStyleLabels,
  MenuItemStyleOptions,
  MenuItemStyleValues,
  MenuItemType,
  SubKeyboardType,
  SubKeyboardTypeLabels,
} from '@interfaces/menu-item-type';
import type { TelegramGroup } from '@interfaces/telegram-group';
import type { TelegramMenuItem } from '@interfaces/telegram-menu-item';
import type { TelegramMenuItemTranslation } from '@interfaces/telegram-menu-item-translation';
import ConfirmNavigation from '@pages/components/ConfirmNavigation';
import useEditableForm from '@pages/Management/hooks/use-editable-form';
import { ACTIONS } from '@pages/Management/MenuManagement/action';
import withStopPropagation from '@pages/Management/utils/ui/with-stop-propagation';
import { isSuccessfulDataResponse } from '@services/fetcher';
import type { LoaderData } from '@services/types/loader-data';
import fetchConversations from '@utils/api/fetchConversations';
import { Badge, Button, Form, Popconfirm, Space, Table, Tooltip } from 'antd';
import DomPurify from 'dompurify';
import type { ExpandedRowRender } from 'rc-table/lib/interface';
import type { CSSProperties, FC, HTMLAttributes, Key } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';

import EditableCell from '../EditableCell';
import type { EditableCellProps } from '../EditableCell/EditableCell';
import type { loader } from './loader';
import MenuItemAddForm from './MenuItemAddForm';
import MenuItemAddTranslationForm from './MenuItemAddTranslationForm';
import styles from './MenuItemManagement.module.scss';

interface RawContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RawContext = createContext<RawContextProps>({});

const DragHandle: FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RawContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: FC<RowProps> = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style: CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RawContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RawContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RawContext.Provider>
  );
};

const MILLISECONDS_IN_MINUTE = 60 * 1000;
const NOTIFY_COOLDOWN_IN_MINUTES = 10;

interface MenuItemForm extends Record<string, unknown> {
  frontend_label: string;
  has_subkeyboard: boolean;
  subkeyboard_type?: TelegramMenuItem['subkeyboard_type'];
  type: 'button' | 'command';
  style?: TelegramMenuItem['style'];
  command: string | null;
}

interface MenuItemTranslationForm extends Record<string, unknown> {
  label: string;
  answer: string;
  language_code: string;
}

const MenuItemManagement: React.FC = () => {
  const auth = useAuth();
  const fetchedMenuItems = useLoaderData() as LoaderData<typeof loader>;
  const [menuItems, setMenuItems] = useState<TelegramMenuItem[]>();
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  useEffect(() => {
    setMenuItems(fetchedMenuItems.data);
    setHasPendingChanges(fetchedMenuItems.meta.isDraft);
  }, [fetchedMenuItems]);

  const {
    form: menuItemForm,
    isLoadingOrSubmitting: isLoadingOrSubmittingMenuItem,
    isRecordEdited: isMenuItemEdited,
    cancelEditing: cancelMenuItemEditing,
    setRecordEditable: setMenuItemEditable,
    submitForm: submitMenuItemForm,
    submitFunction,
    resetFetcher: resetMenuItemFetcher,
  } = useEditableForm<TelegramMenuItem, MenuItemForm>(
    'id',
    [
      'type',
      'style',
      'command',
      'frontend_label',
      'has_subkeyboard',
      'subkeyboard_type',
      'service_group_id',
    ],
    'menu-item-fetcher',
    true,
  );
  const {
    form: menuItemTranslationForm,
    isLoadingOrSubmitting: isLoadingOrSubmittingMenuItemTranslation,
    isRecordEdited: isMenuItemTranslationEdited,
    cancelEditing: cancelMenuItemTranslationEditing,
    setRecordEditable: setMenuItemTranslationEditable,
    submitForm: submitMenuItemTranslationForm,
    resetFetcher: resetMenuItemTranslationFetcher,
  } = useEditableForm<TelegramMenuItemTranslation, MenuItemTranslationForm>(
    ['item_id', 'language_code'],
    ['label', 'answer', 'language_code'],
    'menu-item-translation-fetcher',
    true,
  );
  const cellItemType = Form.useWatch<MenuItemForm['type']>('type', menuItemForm);
  const cellItemHasSubKeyboard = Form.useWatch<MenuItemForm['has_subkeyboard']>('has_subkeyboard', menuItemForm);

  useEffect(() => {
    if (cellItemType === 'button') {
      // @ts-expect-error-next-line https://ant.design/docs/react/faq#use-forms-setfieldsvalue-method-to-report-an-error-if-the-object-type-contains-null
      menuItemForm.setFieldsValue({
        command: null,
      });
    } else {
      // @ts-expect-error-next-line https://ant.design/docs/react/faq#use-forms-setfieldsvalue-method-to-report-an-error-if-the-object-type-contains-null
      menuItemForm.setFieldsValue({
        has_subkeyboard: false,
        style: null,
      });
    }

    if (!cellItemHasSubKeyboard) {
      // @ts-expect-error-next-line https://ant.design/docs/react/faq#use-forms-setfieldsvalue-method-to-report-an-error-if-the-object-type-contains-null
      menuItemForm.setFieldsValue({
        subkeyboard_type: null,
      });
    }
  }, [cellItemType, cellItemHasSubKeyboard, menuItemForm]);

  const isLoadingOrSubmitting = isLoadingOrSubmittingMenuItem || isLoadingOrSubmittingMenuItemTranslation;

  const [isMenuItemAddModalOpen, setMenuItemAddModalOpen] = useState(false);
  const [isMenuItemTranslationAddModalOpen, setMenuItemTranslationAddModalOpen] = useState(false);
  const [addMenuItemTranslationId, setAddMenuItemTranslationId] = useState(0);
  const [addMenuItemTranslationLanguages, setAddMenuItemTranslationLanguages] = useState(languages);
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly Key[]>([]);
  const [conversations, setConversations] = useState<TelegramGroup[]>();
  const [isNotifyOnCooldown, toggleNotifyCooldown] = useCooldown(
    'last-menu-notify',
    NOTIFY_COOLDOWN_IN_MINUTES * MILLISECONDS_IN_MINUTE,
  );

  useEffect(() => {
    async function fetchGroups() {
      const response = await fetchConversations(auth, 'filter[is_authorized]=true', null, null);
      if (!isSuccessfulDataResponse(response)) {
        return;
      }

      setConversations(response.data);
    }

    // noinspection JSIgnoredPromiseFromCall
    fetchGroups();
  }, [auth]);

  const onTriggerCreateMenuItem = useCallback((menuItem: Omit<TelegramMenuItem, 'translations'>) => {
    setExpandedRowKeys([menuItem.id as number]);
    setMenuItemAddModalOpen(false);
  }, []);

  const onTriggerCreateMenuItemTranslation = useCallback((translation: TelegramMenuItemTranslation) => {
    setExpandedRowKeys([translation.item_id]);
    setMenuItemTranslationAddModalOpen(false);
  }, []);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over !== null && active.id !== over.id) {
      setMenuItems((prevState) => {
        if (!prevState) {
          return prevState;
        }
        const activeIndex = prevState?.findIndex((record) => record.id === active.id);
        const overIndex = prevState?.findIndex((record) => record.id === over.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
      submitFunction(
        {
          intent: ACTIONS.SwapOrder,
          src_id: active.id,
          dst_id: over?.id,
        },
        {
          method: 'POST',
          encType: 'application/json',
        },
      );
    }
  };

  const expandedRowRender: ExpandedRowRender<TelegramMenuItem> = (record) => {
    const translations = record.translations.toSorted((translation1, translation2) =>
      languages[translation1.language_code].localeCompare(languages[translation2.language_code]),
    );
    const existingLanguages = translations.map((translation) => translation.language_code);

    return (
      <Form form={menuItemTranslationForm} component={false}>
        <Table<TelegramMenuItemTranslation>
          className={styles.translationTable}
          dataSource={translations}
          rowKey={(record) => `${record.item_id}_${record.language_code}`}
          pagination={false}
          rowClassName={() => `editable-row ${styles.translationRow}`}
          loading={isLoadingOrSubmitting}
          components={{
            body: { cell: EditableCell },
          }}
        >
          <Table.Column<TelegramMenuItemTranslation>
            key="label"
            dataIndex="label"
            title="Label"
            onCell={(record): Omit<EditableCellProps, 'children'> => ({
              dataIndex: 'label',
              inputType: 'text',
              title: 'Label',
              editing: isMenuItemTranslationEdited(record),
              max: 64,
            })}
          />
          <Table.Column<TelegramMenuItemTranslation>
            key="answer"
            dataIndex="answer"
            title="Telegram message"
            onCell={(record): Omit<EditableCellProps, 'children'> => ({
              dataIndex: 'answer',
              inputType: 'rte',
              title: 'Telegram message',
              editing: isMenuItemTranslationEdited(record),
              required: false,
            })}
            render={(htmlContent: string) => (
              <div dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(htmlContent) }} />
            )}
          />
          <Table.Column<TelegramMenuItemTranslation>
            key="language_code"
            dataIndex="language_code"
            title="Language"
            onCell={(record): Omit<EditableCellProps, 'children'> => ({
              dataIndex: 'language_code',
              inputType: 'select',
              selectOptions: Object.entries(languages)
                .filter(([key]) => key === record.language_code || !existingLanguages.includes(key))
                .map(([key, value]) => ({
                  value: key,
                  label: value,
                })),
              title: 'Language',
              editing: record.language_code !== 'en' && isMenuItemTranslationEdited(record),
            })}
            render={(value: string) => {
              return value in languages ? languages[value] : value;
            }}
          />
          <Table.Column<TelegramMenuItemTranslation>
            key="actions"
            title="Actions"
            render={(_, record) => {
              const editable = isMenuItemTranslationEdited(record);
              return (
                <Space size="middle">
                  {editable ? (
                    <>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        title="Save"
                        onClick={async () => {
                          // noinspection JSIgnoredPromiseFromCall
                          await submitMenuItemTranslationForm(record, ACTIONS.UpdateMenuItemTranslation, {
                            language_code_previous: record.language_code,
                          });
                        }}
                      />
                      <Button
                        type="default"
                        icon={<StopOutlined />}
                        title="Cancel"
                        onClick={() => {
                          resetMenuItemTranslationFetcher();
                          cancelMenuItemTranslationEditing();
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        type="default"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setMenuItemTranslationEditable(record);
                        }}
                      />
                      <Popconfirm
                        title="Sure to delete?"
                        onConfirm={() => {
                          // noinspection JSIgnoredPromiseFromCall
                          submitMenuItemTranslationForm(
                            {
                              item_id: record.item_id,
                              language_code: record.language_code,
                            },
                            ACTIONS.DeleteMenuItemTranslation,
                            undefined,
                            'DELETE',
                          );
                        }}
                        disabled={record.language_code === 'en'}
                      >
                        <Button danger icon={<DeleteOutlined />} disabled={record.language_code === 'en'} />
                      </Popconfirm>
                    </>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </Form>
    );
  };

  return (
    <>
      <ConfirmNavigation
        needsConfirm={hasPendingChanges}
        modalText="You have menu changes that haven't been sent to Telegram yet"
      />
      <MenuItemAddForm
        open={isMenuItemAddModalOpen}
        onCreate={onTriggerCreateMenuItem}
        onCancel={() => {
          setMenuItemAddModalOpen(false);
        }}
      />
      <MenuItemAddTranslationForm
        open={isMenuItemTranslationAddModalOpen}
        onCreate={onTriggerCreateMenuItemTranslation}
        onCancel={() => {
          setMenuItemTranslationAddModalOpen(false);
        }}
        item_id={addMenuItemTranslationId}
        languages={addMenuItemTranslationLanguages}
      />
      <div className={styles.menuItems}>
        <Tooltip
          styles={{ root: { whiteSpace: 'pre-line' } }}
          title={`${hasPendingChanges ? 'You have menu updates that are not in Telegram yet!\n' : ''}Publish menu to all conversations (allowed once every ${NOTIFY_COOLDOWN_IN_MINUTES} minutes)`}
        >
          <Button
            danger={hasPendingChanges}
            icon={isNotifyOnCooldown ? <SyncOutlined spin /> : undefined}
            disabled={isLoadingOrSubmitting || isNotifyOnCooldown}
            onClick={() => {
              toggleNotifyCooldown();
              submitFunction(
                {
                  intent: ACTIONS.Notify,
                },
                {
                  method: 'POST',
                  encType: 'application/json',
                },
              );
            }}
          >
            Publish to Telegram
          </Button>
        </Tooltip>
        <Button
          type="primary"
          onClick={() => {
            setMenuItemAddModalOpen(true);
          }}
          disabled={isLoadingOrSubmitting}
        >
          Add Menu Item
        </Button>
      </div>
      <Form form={menuItemForm} component={false}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={(menuItems || []).map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <Table<TelegramMenuItem>
              className={styles.menuItemTable}
              dataSource={menuItems}
              rowKey="id"
              loading={isLoadingOrSubmitting || conversations === undefined}
              rowClassName={() => `editable-row ${styles.menuItemRow}`}
              components={{
                body: { row: Row, cell: EditableCell },
              }}
              expandable={{
                expandedRowRender,
                expandedRowClassName: () => styles.expandedMenuItemRow,
                defaultExpandedRowKeys: [],
                expandedRowKeys,
                onExpandedRowsChange: (expandedKeys) => {
                  setExpandedRowKeys(expandedKeys);
                },
                rowExpandable: (record) => !isMenuItemEdited(record),
                expandRowByClick: true,
              }}
              pagination={false}
            >
              <Table.Column<TelegramMenuItem>
                key="sort_order"
                title="Order"
                align="center"
                width="80"
                render={() => <DragHandle />}
              />
              <Table.Column<TelegramMenuItem> key="id" title="ID" dataIndex="id" />
              <Table.Column<TelegramMenuItem>
                key="type"
                title="Type"
                dataIndex="type"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'type',
                  inputType: 'select',
                  title: 'Type',
                  editing: isMenuItemEdited(record),
                  required: true,
                  selectOptions: Object.entries(MenuItemType).map(([key, value]) => ({
                    label: key,
                    value,
                  })),
                })}
                render={(value: MenuItemType) => (value === MenuItemType.Button ? 'Button' : 'Command')}
              />
              <Table.Column<TelegramMenuItem>
                key="style"
                title={
                  <Tooltip title="Telegram controls the exact colors to match each user's theme.">Appearance</Tooltip>
                }
                dataIndex="style"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'style',
                  inputType: 'select',
                  title: 'Appearance',
                  editing: isMenuItemEdited(record),
                  required: false,
                  selectOptions: MenuItemStyleOptions,
                  selectProps: {
                    disabled: cellItemType !== MenuItemType.Button,
                  },
                  customRules: [
                    {
                      enum: MenuItemStyleValues,
                      message: 'Please select a supported appearance.',
                    },
                  ],
                })}
                render={(value: TelegramMenuItem['style']) => (value ? MenuItemStyleLabels[value] : 'Default')}
              />
              <Table.Column<TelegramMenuItem>
                key="command"
                title="Command"
                dataIndex="command"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'command',
                  inputType: 'text',
                  title: 'Command',
                  editing: isMenuItemEdited(record),
                  required: cellItemType === 'command',
                  disabled: cellItemType !== 'command',
                  max: 32,
                  customRules: [
                    {
                      pattern: /^[a-z0-9_]+$/,
                      message: 'The command can only contain lowercase English letters, digits and underscores.',
                    },
                  ],
                })}
              />
              <Table.Column<TelegramMenuItem>
                key="frontend_label"
                title="Web-client message"
                dataIndex="frontend_label"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'frontend_label',
                  inputType: 'text',
                  title: 'Web-client message',
                  editing: isMenuItemEdited(record),
                  required: false,
                })}
              />
              <Table.Column<TelegramMenuItem>
                key="has_subkeyboard"
                title="Subkeyboard"
                dataIndex="has_subkeyboard"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'has_subkeyboard',
                  inputType: 'switch',
                  title: 'Subkeyboard',
                  editing: isMenuItemEdited(record),
                  required: false,
                  switchProps: {
                    disabled: cellItemType !== 'button',
                  },
                })}
                render={(_, record) => (
                  <Badge
                    status={record.has_subkeyboard ? 'success' : 'error'}
                    text={record.has_subkeyboard ? 'Yes' : 'No'}
                  />
                )}
              />
              <Table.Column<TelegramMenuItem>
                key="subkeyboard_type"
                title="SubKeyboard Type"
                dataIndex="subkeyboard_type"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'subkeyboard_type',
                  inputType: 'select',
                  title: 'SubKeyboard Type',
                  editing: isMenuItemEdited(record),
                  required: false,
                  selectOptions: Object.entries(SubKeyboardType).map(([key, value]) => ({
                    label: SubKeyboardTypeLabels[SubKeyboardType[key as unknown as keyof typeof SubKeyboardType]],
                    value,
                  })),
                  selectProps: {
                    disabled: cellItemType !== 'button' || !cellItemHasSubKeyboard,
                  },
                  customRules: [
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const required =
                          getFieldValue('type') === 'button' && getFieldValue('has_subkeyboard') === true;
                        if (!required || value) {
                          return Promise.resolve();
                        }

                        return Promise.reject(new Error('SubKeyboard type is required when sub keyboard is enabled.'));
                      },
                    }),
                  ],
                })}
                render={(value: SubKeyboardType) => SubKeyboardTypeLabels[value]}
              />
              <Table.Column<TelegramMenuItem>
                key="service_group_id"
                title="Service Request Archive"
                dataIndex="service_group_id"
                onCell={(record): Omit<EditableCellProps, 'children'> => ({
                  dataIndex: 'service_group_id',
                  inputType: 'select',
                  selectOptions: conversations?.map((conversation) => ({
                    value: conversation.id,
                    label: conversation.name,
                  })),
                  selectProps: {
                    allowClear: true,
                  },
                  title: 'Service Request Archive',
                  editing: isMenuItemEdited(record),
                  required: false,
                  inputStyle: { minWidth: '300px', maxWidth: '300px' },
                })}
                render={(_, record) => {
                  if (!record.service_group_id) {
                    return <span style={{ color: '#aaa' }}>Select conversation (optional)</span>;
                  }

                  const conversationName = (conversations || []).find(
                    (conversation) => conversation.id === record.service_group_id,
                  )?.name;

                  return (
                    conversationName && (
                      <Link
                        title={conversationName}
                        to={{
                          pathname: `/chat/conversations/${record.service_group_id}`,
                        }}
                      >
                        {trimText(conversationName, 32)}
                      </Link>
                    )
                  );
                }}
              />
              <Table.Column<TelegramMenuItem>
                key="actions"
                title="Actions"
                render={(_, record) => {
                  const editable = isMenuItemEdited(record);
                  return (
                    <Space size="middle">
                      {editable ? (
                        <>
                          <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            title="Save"
                            onClick={withStopPropagation(async () => {
                              await submitMenuItemForm(record, ACTIONS.UpdateMenuItem);
                            })}
                          />
                          <Button
                            type="default"
                            icon={<StopOutlined />}
                            title="Cancel"
                            onClick={withStopPropagation(() => {
                              resetMenuItemFetcher();
                              cancelMenuItemEditing();
                            })}
                          />
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            title="Add Translation"
                            onClick={withStopPropagation(() => {
                              setAddMenuItemTranslationId(record.id);
                              setAddMenuItemTranslationLanguages(
                                Object.fromEntries(
                                  Object.entries(languages).filter(
                                    ([key]) =>
                                      !record.translations
                                        .map((translation) => translation.language_code)
                                        .includes(key),
                                  ),
                                ),
                              );
                              setMenuItemTranslationAddModalOpen(true);
                            })}
                          />
                          <Button
                            type="default"
                            icon={<EditOutlined />}
                            title="Edit"
                            onClick={withStopPropagation(() => setMenuItemEditable(record))}
                          />
                          <Popconfirm
                            title="Sure to delete menu item with all of it's translations?"
                            onConfirm={withStopPropagation(() => {
                              // noinspection JSIgnoredPromiseFromCall
                              submitMenuItemForm({ id: record.id }, ACTIONS.DeleteMenuItem, undefined, 'DELETE');
                            })}
                            onCancel={withStopPropagation()}
                            onPopupClick={withStopPropagation()}
                          >
                            <Button danger title="Delete" icon={<DeleteOutlined />} onClick={withStopPropagation()} />
                          </Popconfirm>
                        </>
                      )}
                    </Space>
                  );
                }}
              />
            </Table>
          </SortableContext>
        </DndContext>
      </Form>
    </>
  );
};

export default MenuItemManagement;
